import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Group,
  Button,
  Badge,
  Stack,
  Skeleton,
  Divider,
  ActionIcon,
  Breadcrumbs,
  Anchor,
  Avatar,
  Card,
  Grid,
  Menu,
  ScrollArea,
  Modal,
  Pagination,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconDotsVertical,
  IconTrash,
  IconRefresh,
  IconUserMinus,
  IconHistory,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { NonPaginatedResponse } from "../../../lib/types";
import { HttpCodes } from "../../../lib/data";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions } from "../../../lib/data";
import {
  ISquadDetailsProps,
  ISquadMemberProps,
  ISquadMembershipHistoryEntryProps,
  ISquadProps,
  ISquadLedgerEntryProps,
  squadApiId,
} from "../../../lib/types/Squad";
import { IUserProps } from "../../../lib/types/User";

const HISTORY_EVENT_LABELS: Record<string, string> = {
  joined: "Joined as active member",
  join_request_submitted: "Requested to join (pending leader approval)",
  join_request_rejected: "Join request rejected by leader",
  invite_sent: "Invited to squad (pending acceptance)",
  invite_withdrawn: "Invite declined / withdrawn before joining",
  join_request_withdrawn: "Join request withdrawn before approval",
  left: "Left the squad voluntarily",
  removed_by_admin: "Removed by platform admin",
  removed_by_leader: "Removed by squad leader",
  squad_disbanded: "Membership ended — squad disbanded",
};

function normalizeMembershipHistory(
  events: unknown[] | undefined
): ISquadMembershipHistoryEntryProps[] {
  if (!events?.length) return [];
  return events.map((ev) => {
    const row = ev as Record<string, unknown> & {
      user?: { id?: string; _id?: string; firstName?: string; lastName?: string; email?: string };
      actorUserId?: { id?: string; _id?: string; firstName?: string; lastName?: string; email?: string } | null;
    };
    const u = row.user;
    const uid = u ? squadApiId(u) : undefined;
    const au =
      row.actorUserId && typeof row.actorUserId === "object"
        ? (row.actorUserId as { id?: string; _id?: string })
        : null;
    const actorUid = au ? squadApiId(au) : undefined;
    const created = row.createdAt as string | Date | undefined;
    return {
      id: String(row._id ?? row.id ?? ""),
      type: row.type as ISquadMembershipHistoryEntryProps["type"],
      createdAt:
        typeof created === "string"
          ? created
          : created
            ? new Date(created).toISOString()
            : "",
      user:
        u && uid
          ? { ...(u as IUserProps), id: uid }
          : null,
      actorUser:
        au && actorUid
          ? { ...(row.actorUserId as IUserProps), id: actorUid }
          : null,
      actorAdminId: (row.actorAdminId as string | null | undefined) ?? null,
      role: row.role as ISquadMembershipHistoryEntryProps["role"],
    };
  });
}

const SquadDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);
  const [disbandModalOpen, setDisbandModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ISquadMemberProps | null>(null);
  const [cooldownTarget, setCooldownTarget] = useState<IUserProps | null>(null);
  const [ledgerPage, setLedgerPage] = useState(1);

  const squadIdParam = id && id !== "undefined" ? id : undefined;

  useEffect(() => {
    setLedgerPage(1);
  }, [squadIdParam]);

  const ledgerEndpoint = useMemo(() => {
    if (!squadIdParam) {
      return { route: "", method: "GET" as const };
    }
    return Endpoints.GetSquadPointsLedger(squadIdParam, {
      page: ledgerPage,
      limit: 15,
    });
  }, [squadIdParam, ledgerPage]);

  const { data: ledgerAxiosBody, isLoading: ledgerLoading } = usePerformRequest<{
    data?: ISquadLedgerEntryProps[];
    pagination?: { total: number; page: number; pages: number; limit: number };
  }>({
    method: "GET",
    route: ledgerEndpoint.route,
    skip: !squadIdParam,
  });

  const { data, isLoading, reloadData } = usePerformRequest<
    NonPaginatedResponse<
      | ISquadDetailsProps
      | {
          squad: ISquadProps;
          members: ISquadMemberProps[];
          membershipHistory?: unknown[];
        }
    >
  >({
    method: "GET",
    route: squadIdParam ? Endpoints.GetSquadDetails(squadIdParam).route : "",
    showError: true,
    skip: !squadIdParam,
  });

  const squad = useMemo((): ISquadDetailsProps | undefined => {
    const payload = data as { data?: unknown } | undefined;
    const raw = payload?.data;
    if (!raw || typeof raw !== "object") return undefined;

    if ("squad" in raw && "members" in raw) {
      const {
        squad: rawSquad,
        members,
        membershipHistory: rawHistory,
        recentPointsLedger,
      } = raw as {
        squad: ISquadProps & {
          _id?: string;
          squadPointsTotal?: number;
          squadPointsEpoch?: number;
        };
        members: (ISquadMemberProps & { _id?: string; squadPointsContribution?: number })[];
        membershipHistory?: unknown[];
        recentPointsLedger?: ISquadLedgerEntryProps[];
      };
      const sid = squadApiId(rawSquad);
      if (!sid) return undefined;
      const normalizedMembers = (members ?? []).map((m) => {
        const u = m.user as { id?: string; _id?: string };
        const uid = squadApiId(u);
        return {
          ...m,
          id: m.id ?? String(m._id),
          squadPointsContribution: m.squadPointsContribution ?? 0,
          user: uid ? { ...m.user, id: uid } : m.user,
        };
      });
      return {
        ...rawSquad,
        id: sid,
        squadPointsTotal: rawSquad.squadPointsTotal,
        squadPointsEpoch: rawSquad.squadPointsEpoch,
        members: normalizedMembers,
        membersCount:
          rawSquad.membersCount ??
          normalizedMembers.length,
        membershipHistory: normalizeMembershipHistory(rawHistory),
        recentPointsLedger: recentPointsLedger ?? [],
      };
    }

    const flat = raw as ISquadDetailsProps & { _id?: string; membershipHistory?: unknown[] };
    const sid = squadApiId(flat);
    if (!sid) return undefined;
    return {
      ...flat,
      id: sid,
      membershipHistory: normalizeMembershipHistory(flat.membershipHistory),
    };
  }, [data]);

  useEffect(() => {
    if (squad?.name) {
      document.title = `${squad.name} | Squad Details | Virel Admin`;
    } else {
      document.title = "Squad Details | Virel Admin";
    }
  }, [squad]);

  const formatNgn = (n: number) =>
    `₦${new Intl.NumberFormat("en-NG", { maximumFractionDigits: 0 }).format(n)}`;

  const handleRemoveMember = (member: ISquadMemberProps) => {
    setRemoveTarget(member);
  };

  const executeRemoveMember = async () => {
    if (!removeTarget || !squadIdParam) return;
    setIsProcessing(true);
    const endpoint = Endpoints.AdminRemoveMember(squadIdParam, removeTarget.user.id);
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
    });
    setIsProcessing(false);
    if (r.status === HttpCodes.Success || r.status === HttpCodes.NoContent) {
      setRemoveTarget(null);
      await reloadData();
    }
  };

  const handleResetCooldown = (user: IUserProps) => {
    setCooldownTarget(user);
  };

  const executeResetCooldown = async () => {
    if (!cooldownTarget) return;
    setIsProcessing(true);
    const endpoint = Endpoints.ResetUserCooldown(cooldownTarget.id);
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
    });
    setIsProcessing(false);
    if (r.status === HttpCodes.Success) {
      setCooldownTarget(null);
    }
  };

  const executeDisband = async () => {
    if (!squadIdParam) return;
    setIsProcessing(true);
    const endpoint = Endpoints.AdminDisbandSquad(squadIdParam);
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
    });
    setIsProcessing(false);
    if (r.status === HttpCodes.Success || r.status === HttpCodes.NoContent) {
      setDisbandModalOpen(false);
      navigate("/d/squads");
    }
  };

  const fullLedgerRows = ledgerAxiosBody?.data ?? [];
  const ledgerPagination = ledgerAxiosBody?.pagination;

  if (isLoading && !squad) {
    return (
      <Container size="xl">
        <Skeleton height={40} width="30%" mb="xl" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Skeleton height={300} radius="md" />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Skeleton height={500} radius="md" />
          </Grid.Col>
        </Grid>
      </Container>
    );
  }

  if (!squad) {
    return (
      <Container size="xl">
        <Title order={2}>Squad not found</Title>
        <Button onClick={() => navigate("/d/squads")} mt="md">Back to Squads</Button>
      </Container>
    );
  }

  const items = [
    { title: "Dashboard", href: "/d" },
    { title: "Squads", href: "/d/squads" },
    { title: squad.name, href: "#" },
  ].map((item, index) => (
    <Anchor href={item.href} key={index} onClick={(e) => {
      if (item.href !== "#") {
        e.preventDefault();
        navigate(item.href);
      }
    }}>
      {item.title}
    </Anchor>
  ));

  return (
    <PermissionGuard permission={AdminPermissions.MANAGE_USERS}>
      <Container size="xl">
        <Stack gap="lg">
          <Breadcrumbs>{items}</Breadcrumbs>

          <Group justify="space-between">
            <Group>
              <ActionIcon variant="subtle" onClick={() => navigate("/d/squads")}>
                <IconArrowLeft size={20} />
              </ActionIcon>
              <Title order={1}>{squad.name}</Title>
              <Badge color={squad.status === "active" ? "green" : "red"}>
                {squad.status.toUpperCase()}
              </Badge>
            </Group>
            <Group>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => reloadData()}
                loading={isProcessing}
              >
                Refresh
              </Button>
              <Button
                color="red"
                variant="filled"
                leftSection={<IconTrash size={16} />}
                onClick={() => setDisbandModalOpen(true)}
                disabled={squad.status === "disbanded"}
              >
                Disband Squad
              </Button>
            </Group>
          </Group>

          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card withBorder radius="md" p="xl">
                <Stack gap="md">
                  <Title order={3}>Squad Info</Title>
                  <Divider />
                  <div>
                    <Text size="xs" c="dimmed">Invite Code</Text>
                    <Text fw={700} size="lg" ff="monospace">{squad.inviteCode}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Created At</Text>
                    <Text fw={500}>{new Date(squad.createdAt).toLocaleString()}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Description</Text>
                    <Text size="sm">{squad.description || "No description provided"}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Members Count</Text>
                    <Text fw={500}>{squad.membersCount} / 5</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Squad points (current)</Text>
                    <Text fw={700} size="lg" c="teal">
                      {formatNgn(squad.squadPointsTotal ?? 0)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Epoch {squad.squadPointsEpoch ?? 0}. Disbanding advances the epoch so public squad totals reset to zero; ledger history is retained for audit.
                    </Text>
                  </div>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 8 }}>
              <Paper withBorder radius="md">
                <Table striped highlightOnHover verticalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Member</Table.Th>
                      <Table.Th>Level</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Squad pts (tenure)</Table.Th>
                      <Table.Th>Joined At</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {squad.members.map((member) => {
                      const mStatus = member.status || "active";
                      const statusColor =
                        mStatus === "active"
                          ? "green"
                          : mStatus === "pending_admin_approval"
                            ? "orange"
                            : mStatus === "pending_user_acceptance"
                              ? "grape"
                              : "gray";
                      return (
                      <Table.Tr key={member.id}>
                        <Table.Td>
                          <Group gap="sm">
                            <Avatar size="sm" color="brand" radius="xl">
                              {member.user?.firstName?.charAt(0) || "?"}
                            </Avatar>
                            <div>
                              <Text size="sm" fw={500}>
                                {member.user?.firstName || "Unknown"} {member.user?.lastName || ""}
                              </Text>
                              <Text size="xs" c="dimmed">{member.user?.email}</Text>
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge color="yellow" variant="light" size="sm">
                            Level {member.user?.level ?? 1}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={member.role === "leader" ? "yellow" : "blue"} variant="light">
                            {member.role.toUpperCase()}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={statusColor} variant="light" tt="uppercase" size="sm">
                            {mStatus.replace(/_/g, " ")}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={600}>
                            {formatNgn(member.squadPointsContribution ?? 0)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{new Date(member.joinedAt).toLocaleDateString()}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Menu shadow="md" width={200}>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray">
                                <IconDotsVertical size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconHistory size={14} />}
                                onClick={() => handleResetCooldown(member.user)}
                              >
                                Reset Cooldown
                              </Menu.Item>
                              {member.role !== "leader" && (
                                <Menu.Item
                                  leftSection={<IconUserMinus size={14} />}
                                  color="red"
                                  onClick={() => handleRemoveMember(member)}
                                >
                                  Remove from Squad
                                </Menu.Item>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Paper>
            </Grid.Col>
          </Grid>

          <Paper withBorder radius="md" p="md">
            <Stack gap="sm">
              <Title order={3}>Membership history</Title>
              <Text size="sm" c="dimmed">
                Full timeline: active joins, join requests, invite flows, voluntary departures,
                leader rejections, admin removals, and disbands. Rows are recorded from when this
                feature is live; membership changes that happened before then do not appear here.
              </Text>
              {(squad.membershipHistory ?? []).length === 0 ? (
                <Text size="sm" c="dimmed" py="md">
                  No recorded events yet. New activity will appear here as it happens.
                </Text>
              ) : (
                <ScrollArea h={440} type="auto">
                  <Table striped highlightOnHover verticalSpacing="sm" layout="fixed">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={170}>When</Table.Th>
                        <Table.Th>User</Table.Th>
                        <Table.Th>Event</Table.Th>
                        <Table.Th w={120}>Role</Table.Th>
                        <Table.Th>Actor / source</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(squad.membershipHistory ?? []).map((entry) => (
                        <Table.Tr key={entry.id}>
                          <Table.Td>
                            <Text size="sm">{new Date(entry.createdAt).toLocaleString()}</Text>
                          </Table.Td>
                          <Table.Td>
                            {entry.user ? (
                              <Group gap="sm" wrap="nowrap">
                                <Avatar size="sm" color="gray" radius="xl">
                                  {entry.user.firstName?.charAt(0) || "?"}
                                </Avatar>
                                <div style={{ minWidth: 0 }}>
                                  <Text size="sm" fw={500} truncate>
                                    {entry.user.firstName || "Unknown"}{" "}
                                    {entry.user.lastName || ""}
                                  </Text>
                                  <Text size="xs" c="dimmed" truncate>
                                    {entry.user.email}
                                  </Text>
                                  <Badge size="xs" variant="light" color="yellow" mt={4}>
                                    Level {entry.user.level ?? 1}
                                  </Badge>
                                </div>
                              </Group>
                            ) : (
                              <Text size="sm" c="dimmed">—</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {HISTORY_EVENT_LABELS[entry.type] ?? entry.type}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {entry.role ? (
                              <Badge size="sm" variant="light" color="gray">
                                {entry.role}
                              </Badge>
                            ) : (
                              <Text size="sm" c="dimmed">—</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={2}>
                              {entry.actorUser
                                ? `${entry.actorUser.firstName || ""} ${entry.actorUser.lastName || ""}`.trim() ||
                                  entry.actorUser.email ||
                                  entry.actorUser.id
                                : entry.actorAdminId
                                  ? "Dashboard admin"
                                  : "—"}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="md">
            <Stack gap="md">
              <Title order={3}>Recent squad point events</Title>
              <Text size="sm" c="dimmed">
                Last 50 ledger rows for this squad (all epochs). Delta uses the same amounts as member payouts, including level multiplier.
              </Text>
              {(squad.recentPointsLedger ?? []).length === 0 ? (
                <Text size="sm" c="dimmed">No ledger entries yet.</Text>
              ) : (
                <ScrollArea h={280} type="auto">
                  <Table striped highlightOnHover verticalSpacing="xs" layout="fixed">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th w={160}>When</Table.Th>
                        <Table.Th>User</Table.Th>
                        <Table.Th>Mission</Table.Th>
                        <Table.Th w={90}>Epoch</Table.Th>
                        <Table.Th w={110}>Kind</Table.Th>
                        <Table.Th w={120}>Delta</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(squad.recentPointsLedger ?? []).map((row) => {
                        const u = row.userId;
                        const userLabel =
                          u && typeof u === "object"
                            ? `${(u as IUserProps).firstName || ""} ${(u as IUserProps).lastName || ""}`.trim() ||
                              (u as IUserProps).email ||
                              "—"
                            : "—";
                        const mt =
                          row.missionId && typeof row.missionId === "object"
                            ? (row.missionId as { title?: string }).title || "—"
                            : "—";
                        return (
                          <Table.Tr key={String(row._id ?? row.createdAt)}>
                            <Table.Td>
                              <Text size="xs">
                                {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td><Text size="xs">{userLabel}</Text></Table.Td>
                            <Table.Td><Text size="xs" lineClamp={2}>{mt}</Text></Table.Td>
                            <Table.Td><Text size="xs">{row.squadPointsEpoch ?? "—"}</Text></Table.Td>
                            <Table.Td><Badge size="xs" variant="light">{row.kind}</Badge></Table.Td>
                            <Table.Td>
                              <Text size="xs" fw={700} c={row.delta >= 0 ? "teal" : "red"}>
                                {formatNgn(row.delta)}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="md">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={3}>Full points ledger</Title>
                {ledgerPagination && ledgerPagination.pages > 1 && (
                  <Pagination
                    size="sm"
                    value={ledgerPage}
                    onChange={setLedgerPage}
                    total={ledgerPagination.pages}
                  />
                )}
              </Group>
              {ledgerLoading ? (
                <Skeleton height={120} />
              ) : fullLedgerRows.length === 0 ? (
                <Text size="sm" c="dimmed">No entries.</Text>
              ) : (
                <ScrollArea h={360} type="auto">
                  <Table striped highlightOnHover verticalSpacing="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>When</Table.Th>
                        <Table.Th>User</Table.Th>
                        <Table.Th>Mission</Table.Th>
                        <Table.Th>Week</Table.Th>
                        <Table.Th>Epoch</Table.Th>
                        <Table.Th>Kind</Table.Th>
                        <Table.Th>Delta</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {fullLedgerRows.map((row) => {
                        const u = row.userId;
                        const userLabel =
                          u && typeof u === "object"
                            ? `${(u as IUserProps).firstName || ""} ${(u as IUserProps).lastName || ""}`.trim() ||
                              (u as IUserProps).email ||
                              "—"
                            : "—";
                        const mt =
                          row.missionId && typeof row.missionId === "object"
                            ? (row.missionId as { title?: string }).title || "—"
                            : "—";
                        return (
                          <Table.Tr key={String(row._id)}>
                            <Table.Td>
                              <Text size="xs">{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</Text>
                            </Table.Td>
                            <Table.Td><Text size="xs">{userLabel}</Text></Table.Td>
                            <Table.Td><Text size="xs" lineClamp={2}>{mt}</Text></Table.Td>
                            <Table.Td><Text size="xs">{row.weekKey}</Text></Table.Td>
                            <Table.Td><Text size="xs">{row.squadPointsEpoch ?? "—"}</Text></Table.Td>
                            <Table.Td><Badge size="xs" variant="outline">{row.kind}</Badge></Table.Td>
                            <Table.Td>
                              <Text size="xs" fw={600} c={row.delta >= 0 ? "teal" : "red"}>
                                {formatNgn(row.delta)}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Stack>
          </Paper>

          <Modal
            opened={disbandModalOpen}
            onClose={() => !isProcessing && setDisbandModalOpen(false)}
            title="Disband this squad?"
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                This permanently disbands <b>{squad.name}</b>, removes all members, and advances the squad points epoch so
                public squad totals reset to <b>zero</b>. Historical ledger rows remain for audit.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" disabled={isProcessing} onClick={() => setDisbandModalOpen(false)}>
                  Cancel
                </Button>
                <Button color="red" loading={isProcessing} onClick={executeDisband}>
                  Disband squad
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal
            opened={!!removeTarget}
            onClose={() => !isProcessing && setRemoveTarget(null)}
            title="Remove member"
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                Remove <b>{removeTarget?.user?.firstName} {removeTarget?.user?.lastName}</b> from this squad? Their historical squad points stay on the squad ledger; if they rejoin, their personal contribution display starts from zero for the new membership.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" disabled={isProcessing} onClick={() => setRemoveTarget(null)}>Cancel</Button>
                <Button color="red" loading={isProcessing} onClick={executeRemoveMember}>
                  Remove member
                </Button>
              </Group>
            </Stack>
          </Modal>

          <Modal
            opened={!!cooldownTarget}
            onClose={() => !isProcessing && setCooldownTarget(null)}
            title="Reset squad cooldown"
            centered
          >
            <Stack gap="md">
              <Text size="sm">
                Clear the 7-day join/create cooldown for <b>{cooldownTarget?.email}</b>?
              </Text>
              <Group justify="flex-end">
                <Button variant="default" disabled={isProcessing} onClick={() => setCooldownTarget(null)}>Cancel</Button>
                <Button loading={isProcessing} onClick={executeResetCooldown}>
                  Reset cooldown
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Stack>
      </Container>
    </PermissionGuard>
  );
};

export default SquadDetails;
